import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

const MOBILE_QUERY = '(max-width: 600px)';
const SUMMARY_SELECTOR = '#gc-dg-summary details';
const RESERVE_PROPERTY = '--checkout-summary-height';

export const useMobileSummarySheet = (panelRef: RefObject<HTMLElement | null>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const summaryRef = useRef<HTMLDetailsElement | null>(null);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const rootStyle = document.documentElement.style;

    const syncExpanded = () => {
      setIsExpanded(Boolean(summaryRef.current?.open) && mediaQuery.matches);
    };

    const reserveCollapsedHeight = () => {
      const summary = summaryRef.current;
      if (!summary || summary.open || !mediaQuery.matches) return;
      rootStyle.setProperty(
        RESERVE_PROPERTY,
        `${Math.ceil(summary.getBoundingClientRect().height)}px`,
      );
    };
    const resizeObserver = new ResizeObserver(reserveCollapsedHeight);

    const attachSummary = () => {
      const summary = panel.querySelector<HTMLDetailsElement>(SUMMARY_SELECTOR);
      if (!summary || summary === summaryRef.current) return;

      if (summaryRef.current) {
        summaryRef.current.removeEventListener('toggle', syncExpanded);
        resizeObserver.unobserve(summaryRef.current);
      }
      summaryRef.current = summary;
      summary.addEventListener('toggle', syncExpanded);
      resizeObserver.observe(summary);
      if (mediaQuery.matches) summary.open = false;
      syncExpanded();
    };

    const handleViewportChange = () => {
      if (summaryRef.current) summaryRef.current.open = !mediaQuery.matches;
      if (!mediaQuery.matches) rootStyle.removeProperty(RESERVE_PROPERTY);
      syncExpanded();
    };

    const mutationObserver = new MutationObserver(attachSummary);
    mutationObserver.observe(panel, { childList: true, subtree: true });
    attachSummary();
    mediaQuery.addEventListener('change', handleViewportChange);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      mediaQuery.removeEventListener('change', handleViewportChange);
      summaryRef.current?.removeEventListener('toggle', syncExpanded);
      summaryRef.current = null;
      rootStyle.removeProperty(RESERVE_PROPERTY);
    };
  }, [panelRef]);

  const collapse = useCallback(() => {
    if (summaryRef.current) summaryRef.current.open = false;
    setIsExpanded(false);
  }, []);

  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') collapse();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, collapse]);

  return { isExpanded, collapse };
};
